// Client/src/pages/digi/Invoice/TemplateDesigner/canvas/useRenderedFrames.js
//
// Renders the design through the server (the same renderer that prints the
// PDF) and shows it in one of two iframes. The new HTML loads into the hidden
// iframe; only when it has loaded does it swap in, so the page never flashes
// blank while you work. After each load the positions of every section,
// element and column are measured (in iframe px) for the overlay.

import { useCallback, useEffect, useRef, useState } from "react";
import { renderTemplateHtml } from "../../Components/templateApi";

const RENDER_DELAY_MS = 300;

export function measure(doc) {
  if (!doc?.body) return null;
  const win = doc.defaultView;
  const sx = win?.scrollX || 0;
  const sy = win?.scrollY || 0;
  const box = (node) => {
    const r = node.getBoundingClientRect();
    return { x: r.left + sx, y: r.top + sy, w: r.width, h: r.height };
  };
  const bands = Array.from(doc.querySelectorAll("[data-band-id]")).map((n) => ({ id: n.getAttribute("data-band-id"), ...box(n) }));
  const elements = Array.from(doc.querySelectorAll("[data-el-id]")).map((n) => {
    const col = n.parentElement?.closest("[data-col-id]");
    const band = n.closest("[data-band-id]");
    return {
      id: n.getAttribute("data-el-id"),
      bandId: band?.getAttribute("data-band-id") || null,
      colId: col && band && band.contains(col) ? col.getAttribute("data-col-id") : null,
      ...box(n),
    };
  });
  const cols = Array.from(doc.querySelectorAll("[data-col-id]")).map((n) => ({
    id: n.getAttribute("data-col-id"),
    rowId: n.closest("[data-el-id]")?.getAttribute("data-el-id") || null,
    ...box(n),
  }));
  const pageNode = doc.querySelector(".rd-page");
  const page = pageNode ? box(pageNode) : null;
  const height = Math.max(doc.documentElement.scrollHeight, doc.body.scrollHeight);
  return { bands, elements, cols, page, height };
}

export default function useRenderedFrames({ templateId, design, sample, enabled = true }) {
  const [slots, setSlots] = useState([{ html: "", n: 0 }, { html: "", n: 0 }]);
  const [active, setActive] = useState(0);
  const [layout, setLayout] = useState(null);
  const [rendering, setRendering] = useState(false);
  const [error, setError] = useState("");
  // Two stable ref objects (one per iframe).
  const frames = useRef([{ current: null }, { current: null }]).current;
  const reqId = useRef(0);
  const pendingSlot = useRef(null);
  const activeRef = useRef(0);

  useEffect(() => {
    if (!enabled || !design) return undefined;
    const id = ++reqId.current;
    setRendering(true);
    const timer = setTimeout(async () => {
      try {
        const { html } = await renderTemplateHtml(templateId, design, sample);
        if (id !== reqId.current) return;
        const target = 1 - activeRef.current;
        pendingSlot.current = target;
        setSlots((s) => {
          const next = [...s];
          next[target] = { html, n: s[target].n + 1 };
          return next;
        });
        setError("");
      } catch (e) {
        if (id === reqId.current) {
          setError(e.message || "Could not draw the preview");
          setRendering(false);
        }
      }
    }, RENDER_DELAY_MS);
    return () => clearTimeout(timer);
  }, [templateId, design, sample, enabled]);

  const remeasure = useCallback(() => {
    const doc = frames[activeRef.current].current?.contentDocument;
    const m = measure(doc);
    if (m) setLayout(m);
  }, [frames]);

  const onFrameLoad = useCallback((slot) => {
    if (pendingSlot.current !== slot) return;
    pendingSlot.current = null;
    const doc = frames[slot].current?.contentDocument;
    const m = measure(doc);
    activeRef.current = slot;
    setActive(slot);
    if (m) setLayout(m);
    setRendering(false);
    // Web fonts can land after load and shift text; measure again then.
    doc?.fonts?.ready?.then(() => {
      if (activeRef.current === slot) {
        const again = measure(doc);
        if (again) setLayout(again);
      }
    });
  }, [frames]);

  const activeDoc = useCallback(() => frames[activeRef.current].current?.contentDocument || null, [frames]);

  return { slots, active, frames, onFrameLoad, layout, rendering, error, remeasure, activeDoc };
}
